import store from '@/store';
import apiService from '../apiService';
import pricingPolicy from './pricingPolicy';

/**
 * 가격 설정을 위한 서비스
 */
const pricingService = {
  /**
   * 가격 설정 작업 후 상품 및 coupangDoc 업데이트 (부분 업데이트 방식)
   * @param {Object} product - 상품 객체
   * @param {Object} pricingData - 가격 설정 작업 결과 데이터
   * @returns {Promise<boolean>} 업데이트 성공 여부
   */
  async updateProductWithCoupangDocPartial(product, pricingData) {
    try {
      if (!product || !product._id) {
        throw new Error('유효하지 않은 상품 정보입니다');
      }

      // 점 표기법을 사용하여 업데이트할 필드 지정
      const updateFields = {
        'pricing': pricingData,
        'pricingStatus': 'completed',
        'updatedAt': Date.now()
      };

      // 옵션 정보 업데이트
      if (product.options && product.options.length > 0 &&
          pricingData.options && pricingData.options.length > 0) {

        product.options.forEach((option, optionIndex) => {
          if (option.optionNumber) {
            const matchedPricing = pricingData.options.find(p => p.optionNumber === option.optionNumber);
            if (matchedPricing) {
              // 점 표기법으로 특정 옵션의 특정 필드만 업데이트
              updateFields[`options.${optionIndex}.price`] = matchedPricing.price;
              updateFields[`options.${optionIndex}.bundleQuantity`] = matchedPricing.bundleQuantity;
              updateFields[`options.${optionIndex}.supplyPrice`] = matchedPricing.supplyPrice;
              updateFields[`options.${optionIndex}.sellingPrice`] = matchedPricing.sellingPrice;
              updateFields[`options.${optionIndex}.purchasePrice`] = matchedPricing.purchasePrice;
              updateFields[`options.${optionIndex}.supplyMargin`] = matchedPricing.supplyMargin;
              updateFields[`options.${optionIndex}.sellingMargin`] = matchedPricing.sellingMargin;
              updateFields[`options.${optionIndex}.pricingStatus`] = 'completed';
            }
          }
        });
      }

      // coupangDoc 업데이트 - 점 표기법 사용 (optionNumber 기반 매칭)
      if (product.coupangDoc && product.coupangDoc.jsonDocument) {
        product.coupangDoc.jsonDocument.forEach((docItem, index) => {
          if (docItem.productPage && docItem.productPage.commonAttributes) {
            // docItem에서 optionNumber 추출하여 해당하는 가격 정보 찾기
            const docItemOptionNumber = this.extractOptionNumberFromDocItem(docItem, index, product);
            const pricingInfo = pricingData.options?.find(p => p.optionNumber === docItemOptionNumber);

            if (pricingInfo) {
              console.log(`jsonDocument[${index}] 업데이트 - 옵션: ${pricingInfo.optionNumber}`);

              // MSRP는 판매가에 기본설정의 msrpMultiplier를 곱해서 설정
              const settings = store.state.settings[0]?.defaultSettings || {};
              const msrpMultiplier = settings.msrpMultiplier || 1.3;
              const roundUnit = settings.roundUnit || 100;
              const msrpValue = Number(Math.round((pricingInfo.sellingPrice * msrpMultiplier) / roundUnit) * roundUnit);

              // 점 표기법으로 coupangDoc 필드 업데이트
              updateFields[`coupangDoc.jsonDocument.${index}.productPage.commonAttributes.purchasePrice`] =
                Number(Math.round(pricingInfo.supplyPrice));
              updateFields[`coupangDoc.jsonDocument.${index}.productPage.commonAttributes.coupangSalePrice`] =
                Number(Math.round(pricingInfo.sellingPrice));

              // 스키마 기반으로 msrp 또는 osrp 필드 결정
              const shouldUseOsrp = this.checkSchemaRequiredField(product, 'osrp');
              const fieldName = shouldUseOsrp ? 'osrp' : 'msrp';
              updateFields[`coupangDoc.jsonDocument.${index}.productPage.commonAttributes.${fieldName}`] = msrpValue;
              console.log(`옵션 ${pricingInfo.optionNumber}의 ${fieldName} 필드 업데이트: ${msrpValue}`);
            } else {
              console.warn(`jsonDocument[${index}]에 해당하는 가격 정보를 찾을 수 없습니다. optionNumber: ${docItemOptionNumber}`);
            }
          }
        });
      }

      // 서버에 부분 업데이트 요청
      await apiService.updateRocketProductPartial(product._id, updateFields);

      // store 상태 부분 업데이트
      store.commit('updateProductPartial', {
        productId: product._id,
        updateFields: updateFields
      });

      return true;
    } catch (error) {
      console.error('가격 설정 정보로 상품 업데이트 중 오류:', error);
      throw error;
    }
  },

  /**
   * 가격 설정 작업 후 상품 및 coupangDoc 업데이트 (이전 방식 - 호환성 유지)
   * @param {Object} product - 상품 객체
   * @param {Object} pricingData - 가격 설정 작업 결과 데이터
   * @returns {Promise<Object>} 업데이트된 상품 객체
   * @deprecated 새로운 updateProductWithCoupangDocPartial 메서드를 사용하세요
   */
  async updateProductWithCoupangDoc(product, pricingData) {
    console.warn('updateProductWithCoupangDoc는 더 이상 사용되지 않습니다. updateProductWithCoupangDocPartial을 사용하세요.');
    return this.updateProductWithCoupangDocPartial(product, pricingData);
  },
  /**
   * 가격 설정 처리 메인 함수
   * @param {Object} product - 상품 객체
   * @param {Object} data - 요청 데이터
   * @returns {Promise<Object>} 처리 결과
   */
  async process(product, data = {}) {
    try {
      // 로딩 상태 설정
      store.dispatch('setProcessing', {
        isProcessing: true,
        message: '가격 설정 작업 중...'
      });

      if (!product || !product.options || product.options.length === 0) {
        throw new Error('처리할 상품 또는 옵션이 없습니다.');
      }

      let pricingResults = null;

      // 가격 설정 모드에 따라 처리
      if (data.mode === 'manual' && data.manualPrices) {
        // 수동 가격 설정
        pricingResults = this.processManualPricing(product, data.manualPrices);
      } else {
        // 자동 가격 설정 (기본값) — 상품에 저장된 정책이 화면 기본세팅보다 앞선다
        const pricingInfo = data.pricingInfo || this.getDefaultPricingInfo(product);
        pricingResults = this.calculatePricing(product, pricingInfo);
      }

      // 결과를 상품 객체에 저장
      const updatedProduct = { ...product };
      updatedProduct.pricing = pricingResults;
      updatedProduct.pricingStatus = 'completed';

      // 옵션 상태 업데이트
      if (updatedProduct.options && updatedProduct.options.length > 0 &&
        pricingResults.options && pricingResults.options.length > 0) {

        console.log(`process: ${updatedProduct.options.length}개 옵션 상태 업데이트 시작`);

        updatedProduct.options.forEach(option => {
          // 옵션 상태만 업데이트 (coupangDoc은 updateProductWithCoupangDocPartial에서 처리)
          option.pricingStatus = 'completed';
        });
      }

      // 로깅 - 상품 레벨의 coupangDoc 업데이트는 updateProductWithCoupangDocPartial에서 처리
      if (updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument &&
        updatedProduct.coupangDoc.jsonDocument.length > 0 && pricingResults.options && pricingResults.options.length > 0) {

        console.log(`process: 상품 레벨의 coupangDoc 업데이트는 updateProductWithCoupangDocPartial에서 처리됩니다.`);

        // 로깅 목적으로 첫 번째 옵션의 가격 정보 출력
        const firstPricing = pricingResults.options[0];
        if (firstPricing) {
          console.log(`- 첫 번째 옵션 가격 정보:`);
          console.log(`- supplyPrice: ${firstPricing.supplyPrice}`);
          console.log(`- sellingPrice: ${firstPricing.sellingPrice}`);
          console.log(`- purchasePrice: ${firstPricing.purchasePrice}`);
        }
      }

      // 새로운 방식: 서비스에서 직접 부분 업데이트
      await this.updateProductWithCoupangDocPartial(updatedProduct, pricingResults);
      console.log('가격 설정 정보로 상품 업데이트 완료');

      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      });

      return {
        status: 'completed',
        type: 'pricingStatus',
        result: pricingResults
      };
    } catch (error) {
      console.error('가격 설정 처리 중 오류:', error);

      // 오류 상태로 상품 상태 부분 업데이트
      if (product && product._id) {
        try {
          await apiService.updateRocketProductPartial(product._id, {
            'pricingStatus': 'error',
            'updatedAt': Date.now()
          });

          // store 상태 부분 업데이트
          store.commit('updateProductPartial', {
            productId: product._id,
            updateFields: {
              'pricingStatus': 'error',
              'updatedAt': Date.now()
            }
          });
        } catch (err) {
          console.error('상품 상태 업데이트 실패:', err);
        }
      }

      // 로딩 상태 해제
      store.dispatch('setProcessing', {
        isProcessing: false,
        message: ''
      });

      return {
        status: 'error',
        type: 'pricingStatus',
        error: error.message
      };
    }
  },

  /**
   * 이 상품에 적용할 가격 정책 가져오기
   *
   * 상품에 저장된 설정(product.pricing.info)이 화면 기본세팅보다 앞선다 — 판단은
   * pricingPolicy 하나가 한다. 예전에는 이 함수가 화면 기본세팅만 읽어서,
   * rocket.sourcing.setPrice 로 넣은 환율이 작업개시 때 조용히 기본값으로 되돌아갔다(2026-09-19 실측).
   *
   * @param {Object|null} product - 상품 객체. 없으면 화면 기본세팅만 쓴다
   * @returns {Object} 가격 정책 정보
   */
  getDefaultPricingInfo(product = null) {
    return pricingPolicy.resolve(product, store.state.settings[0]?.defaultSettings);
  },

  /**
   * 수동 가격 설정 처리
   * @param {Object} product - 상품 객체
   * @param {Array} manualPrices - 수동 가격 설정 데이터
   * @returns {Object} 처리 결과
   */
  processManualPricing(product, manualPrices) {
    // 기본 가격 정책 정보 가져오기 (상품에 저장된 설정 우선)
    const pricingInfo = this.getDefaultPricingInfo(product);

    // 가격 결과 객체 구성
    const pricingResults = {
      info: pricingInfo,
      options: []
    };

    // 옵션별로 수동 가격 데이터 처리
    if (product.options && product.options.length > 0) {
      product.options.forEach((option, index) => {
        // 해당 옵션의 수동 가격 데이터 찾기
        const manualPrice = manualPrices[index] || {};

        // 옵션 가격 정보 구성
        const optionPrice = {
          optionNumber: option.optionNumber,
          optionName: this.getOptionName(option),
          price: manualPrice.price || option.price || 0,
          stock: manualPrice.stock || option.stock || 0,
          bundleQuantity: manualPrice.bundleQuantity || 1,
          supplyPrice: manualPrice.supplyPrice || 0,
          sellingPrice: manualPrice.sellingPrice || 0
        };

        // 마진 계산
        const purchasePrice = pricingPolicy.purchaseCost(optionPrice.price * optionPrice.bundleQuantity, pricingInfo);
        optionPrice.purchasePrice = purchasePrice;
        optionPrice.supplyMargin = optionPrice.supplyPrice - purchasePrice;
        optionPrice.sellingMargin = optionPrice.sellingPrice - optionPrice.supplyPrice;

        pricingResults.options.push(optionPrice);
      });
    }

    return pricingResults;
  },

  /**
   * 자동 가격 계산
   * @param {Object} product - 상품 객체
   * @param {Object} pricingInfo - 가격 정책 정보
   * @returns {Object} 계산 결과
   */
  calculatePricing(product, pricingInfo) {
    // 가격 결과 객체 구성
    const pricingResults = {
      info: pricingInfo,
      options: [],
      bundleInfo: {
        hasBundling: false,
        maxBundleQuantity: 1,
        bundleText: ''
      }
    };

    // 옵션이 없으면 빈 결과 반환
    if (!product.options || product.options.length === 0) {
      return pricingResults;
    }

    let maxBundleQuantity = 1;
    // 소싱 목표가는 가장 싼 옵션 기준이다 — 더 비싼 옵션은 scaleFixedPricing 이 판매가·매입원가를 올린다
    const refCny = product.sourcingSellingPrice ? pricingPolicy.referenceCny(product.options) : null;

    // 각 옵션의 가격 계산
    product.options.forEach(option => {
      const cnyPrice = option.price || 0;

      // 번들 수량 계산 (번들링 설정이 활성화된 경우)
      //
      // 옵션에 이미 정해진 묶음 수량이 있으면 그걸 출발값으로 쓴다.
      // 무조건 1 로 시작하면, 묶음을 지정해둔 상품에 가격 작업을 다시 돌릴 때 묶음이 조용히 풀려
      // 매입가가 1개분으로 내려간다 — 2개 묶음이 8,400 이 아니라 4,200 으로 팔린다.
      // (AIRocketReg.updateOptionPricing 이 2026-09-17 에 같은 이유로 고쳐진 것과 같은 규칙이다)
      const savedBundleQuantity = Number(option.bundleQuantity);
      let bundleQuantity = Number.isFinite(savedBundleQuantity) && savedBundleQuantity >= 1
        ? savedBundleQuantity
        : 1;
      // 소싱에서 묶음 수량이나 판매가가 정해진 상품은 자동 계산으로 덮지 않는다.
      if (pricingInfo.enableBundling && !product.sourcingBundleQuantity && !product.sourcingSellingPrice) {
        bundleQuantity = this.calculateBundleQuantity(
          cnyPrice,
          pricingInfo.exchangeRate,
          pricingInfo.marginRate,
          pricingInfo.coupangMarginRate,
          pricingInfo.bundlingType,
          pricingInfo.minMarginAmount,
          pricingInfo.minCoupangMarginAmount,
          pricingInfo.roundUnit
        );

        // 최대 번들 수량 추적
        if (bundleQuantity > maxBundleQuantity) {
          maxBundleQuantity = bundleQuantity;
        }
      }

      // 매입가 계산 (CNY × 환율 × 번들수량)
      // 소싱 목표 판매가 상품에 소싱 매입원가(sourcingPurchaseCost — 통합통관환율·검수·부가세 포함, CLI 가 계산)가
      // 실려 있으면 그 값을 쓴다. 여기서 다시 계산하지 않는다(소싱 묶음 수량과 다르면 개당 원가 × 묶음 수량).
      const sourcing = product.sourcingSellingPrice
        ? pricingPolicy.scaleFixedPricing({
          sellingPrice: Number(product.sourcingSellingPrice),
          purchaseCost: product.sourcingPurchaseCost || null,
          supplyPrice: product.sourcingSupplyPrice || null
        }, cnyPrice, refCny, bundleQuantity, pricingInfo, product.sourcingBundleQuantity || null)
        : null;
      const purchasePrice = (sourcing && sourcing.purchaseCost)
        ? Number(sourcing.purchaseCost)
        : pricingPolicy.purchaseCost(cnyPrice * bundleQuantity, pricingInfo);

      let supplyPrice, sellingPrice, supplyMargin, sellingMargin;

      // 표준(마진율 기반) 1차 계산
      const marginRateDecimal = pricingInfo.marginRate / 100;
      const coupangRateDecimal = pricingInfo.coupangMarginRate / 100;
      const rawSupplyPriceStd = purchasePrice / (1 - marginRateDecimal);
      const supplyPriceStd = Math.round(rawSupplyPriceStd / pricingInfo.roundUnit) * pricingInfo.roundUnit;
      const rawSellingPriceStd = supplyPriceStd / (1 - coupangRateDecimal);
      const sellingPriceStd = Math.round(rawSellingPriceStd / pricingInfo.roundUnit) * pricingInfo.roundUnit;
      const supplyMarginStd = supplyPriceStd - purchasePrice;

      // 소싱에서 목표 판매가가 정해진 상품은 판매가를 고정하고 공급가만 쿠팡마진율로 푼다 (역마진이면 실패)
      if (product.sourcingSellingPrice) {
        const fixed = pricingPolicy.fixedSellingPricing(sourcing.sellingPrice, purchasePrice, pricingInfo, `옵션 ${option.optionNumber}`, sourcing.supplyPrice,
          store.state.settings[0]?.rocketMarginGate);
        supplyPrice = fixed.supplyPrice;
        sellingPrice = fixed.sellingPrice;
      } else if (pricingInfo.enableMinSupplyMargin && supplyMarginStd < pricingInfo.minSupplyMarginAmount) {
        // 최소 공급 마진 하한 적용: 표준 공급마진이 미만일 때만 보정
        const supplyPriceMin = Math.round((purchasePrice + pricingInfo.minSupplyMarginAmount) / pricingInfo.roundUnit) * pricingInfo.roundUnit;
        const rawSellingPriceMin = supplyPriceMin / (1 - coupangRateDecimal);
        const sellingPriceMin = Math.round(rawSellingPriceMin / pricingInfo.roundUnit) * pricingInfo.roundUnit;

        supplyPrice = supplyPriceMin;
        sellingPrice = sellingPriceMin;

        console.log(`최소 공급 마진 하한 보정 적용 (옵션 ${option.optionNumber}):`, {
          원가: purchasePrice,
          표준공급가: supplyPriceStd,
          표준공급마진: supplyMarginStd,
          최소공급마진: pricingInfo.minSupplyMarginAmount,
          보정공급가: supplyPrice,
          보정판매가: sellingPrice,
          쿠팡마진율: pricingInfo.coupangMarginRate + '%'
        });
      } else {
        supplyPrice = supplyPriceStd;
        sellingPrice = sellingPriceStd;
      }

      // 최종 마진 계산
      supplyMargin = supplyPrice - purchasePrice;
      sellingMargin = sellingPrice - supplyPrice;

      // 결과 저장
      pricingResults.options.push({
        optionNumber: option.optionNumber,
        optionName: this.getOptionName(option),
        price: cnyPrice,
        stock: option.stock || 0,
        bundleQuantity,
        purchasePrice,
        supplyPrice,
        sellingPrice,
        supplyMargin,
        sellingMargin
      });
    });

    // 번들 정보 설정
    if (maxBundleQuantity > 1) {
      pricingResults.bundleInfo = {
        hasBundling: true,
        maxBundleQuantity: maxBundleQuantity,
        bundleText: `${maxBundleQuantity}개 묶음`
      };
      console.log(`번들 정보 생성: ${pricingResults.bundleInfo.bundleText}`);
    }

    return pricingResults;
  },

  /**
   * 번들 수량 계산
   * @param {number} cnyPrice - 위안화 가격
   * @param {number} exchangeRate - 환율
   * @param {number} marginRate - 마진율
   * @param {number} coupangMarginRate - 쿠팡 마진율
   * @param {string} bundlingType - 번들링 타입
   * @param {number} minMarginAmount - 최소 마진 금액
   * @param {number} minCoupangMarginAmount - 최소 쿠팡 마진 금액
   * @param {number} roundUnit - 반올림 단위
   * @returns {number} 계산된 번들 수량
   */
  calculateBundleQuantity(cnyPrice, exchangeRate, marginRate, coupangMarginRate, bundlingType, minMarginAmount, minCoupangMarginAmount, roundUnit = 100) {
    // 단일 상품으로 계산한 가격
    const purchasePrice = cnyPrice * exchangeRate;
    const marginRateDecimal = marginRate / 100;
    const coupangRateDecimal = coupangMarginRate / 100;

    const rawSupplyPrice = purchasePrice / (1 - marginRateDecimal);
    const supplyPrice = Math.round(rawSupplyPrice / roundUnit) * roundUnit;

    const rawSellingPrice = supplyPrice / (1 - coupangRateDecimal);
    const sellingPrice = Math.round(rawSellingPrice / roundUnit) * roundUnit;

    // 마진 계산
    const supplyMargin = supplyPrice - purchasePrice;
    const sellingMargin = sellingPrice - supplyPrice;

    // 번들링 기준에 따른 조건 확인
    let bundleQuantity = 1;

    if (bundlingType === 'marginAmount') {
      // 공급 마진액 기준
      if (supplyMargin < minMarginAmount) {
        // 2개, 4개, 6개... 단위로 번들링
        while (supplyMargin * bundleQuantity < minMarginAmount && bundleQuantity < 20) {
          bundleQuantity += 2;
        }
      }
    } else { // 'coupangMarginAmount'
      // 쿠팡 마진액 기준
      if (sellingMargin < minCoupangMarginAmount) {
        // 2개, 4개, 6개... 단위로 번들링
        while (sellingMargin * bundleQuantity < minCoupangMarginAmount && bundleQuantity < 20) {
          bundleQuantity += 2;
        }
      }
    }

    return bundleQuantity;
  },

  /**
   * 공급가 계산
   * @param {number} cnyPrice - 위안화 가격
   * @param {Object} pricingInfo - 가격 정책 정보
   * @returns {number} 계산된 공급가
   */
  calculateSupplyPrice(cnyPrice, pricingInfo) {
    if (!cnyPrice) return 0;

    // 매입가 = CNY × 환율 (통합통관 모드면 소싱과 같은 공식)
    const purchasePrice = pricingPolicy.purchaseCost(cnyPrice, pricingInfo);

    // 공급가 = 매입가 ÷ (1 - 공급마진율/100)
    const marginRate = pricingInfo.marginRate / 100;
    const rawPrice = purchasePrice / (1 - marginRate);

    return Math.round(rawPrice / pricingInfo.roundUnit) * pricingInfo.roundUnit;
  },

  /**
   * 판매가 계산
   * @param {number} supplyPrice - 공급가
   * @param {Object} pricingInfo - 가격 정책 정보
   * @returns {number} 계산된 판매가
   */
  calculateSellingPrice(supplyPrice, pricingInfo) {
    if (!supplyPrice) return 0;

    // 판매가 = 공급가 ÷ (1 - 쿠팡마진율/100)
    const coupangRate = pricingInfo.coupangMarginRate / 100;
    const rawPrice = supplyPrice / (1 - coupangRate);

    return Math.round(rawPrice / pricingInfo.roundUnit) * pricingInfo.roundUnit;
  },

  /**
   * 옵션명 가져오기
   * @param {Object} option - 옵션 객체
   * @returns {string} 옵션명
   */
  getOptionName(option) {
    if (!option) return '기본 옵션';

    let name = '';
    if (option.option1) {
      name += option.option1.nameTrans || option.option1.name || '';
    }
    if (option.option2) {
      name += ' / ' + (option.option2.nameTrans || option.option2.name || '');
    }
    return name || '기본 옵션';
  },

  /**
   * 가격 정보를 포매팅하여 표시
   * @param {number} value - 가격 값
   * @returns {string} 포매팅된 가격
   */
  formatPrice(value) {
    return new Intl.NumberFormat('ko-KR').format(value || 0);
  },

  /**
   * 마진율 계산
   * @param {number} cost - 원가
   * @param {number} price - 판매가
   * @returns {number} 마진율 (%)
   */
  calculateMarginRate(cost, price) {
    if (!cost || !price || cost >= price) return 0;
    return Math.round(((price - cost) / price) * 100);
  },

  /**
   * docItem에서 optionNumber 추출
   * @param {Object} docItem - coupangDoc의 jsonDocument 항목
   * @param {number} index - 배열 인덱스 (fallback용)
   * @param {Object} product - 현재 처리 중인 상품 객체
   * @returns {string} optionNumber
   */
  extractOptionNumberFromDocItem(docItem, index, product = null) {
    try {
      // exposedAttributes에서 옵션 정보 추출 시도
      if (docItem.productPage && docItem.productPage.commonAttributes && docItem.productPage.commonAttributes.exposedAttributes) {
        // exposedAttributes는 배열이므로 첫 번째 항목의 정보를 사용하여 옵션 식별
        // 실제로는 product.options 배열의 인덱스와 매칭하는 것이 가장 확실함
        return this.getOptionNumberByIndex(index, product);
      }

      // fallback: 인덱스 기반으로 optionNumber 추출
      return this.getOptionNumberByIndex(index, product);
    } catch (error) {
      console.warn(`docItem에서 optionNumber 추출 실패 (index: ${index}):`, error);
      return this.getOptionNumberByIndex(index, product);
    }
  },

  /**
   * 인덱스 기반으로 optionNumber 가져오기
   * @param {number} index - 배열 인덱스
   * @param {Object} product - 현재 처리 중인 상품 객체 (우선 사용)
   * @returns {string} optionNumber
   */
  getOptionNumberByIndex(index, product = null) {
    try {
      // 현재 처리 중인 product가 있으면 우선 사용
      if (product && product.options && product.options[index]) {
        console.log(`현재 처리 중인 product에서 optionNumber 추출: ${product.options[index].optionNumber}`);
        return product.options[index].optionNumber;
      }

      // fallback: store에서 현재 선택된 상품의 옵션 정보 가져오기
      const selectedProduct = store.state.selectedRocketProduct;
      if (selectedProduct && selectedProduct.options && selectedProduct.options[index]) {
        console.log(`store에서 optionNumber 추출: ${selectedProduct.options[index].optionNumber}`);
        return selectedProduct.options[index].optionNumber;
      }
      return null;
    } catch (error) {
      console.warn(`인덱스 ${index}로 optionNumber 가져오기 실패:`, error);
      return null;
    }
  },

  /**
   * 스키마에서 특정 필드가 required인지 확인
   * @param {Object} product - 상품 객체
   * @param {string} fieldName - 확인할 필드명 (예: 'osrp', 'msrp')
   * @returns {boolean} required 여부
   */
  checkSchemaRequiredField(product, fieldName) {
    try {
      if (!product.rocketRegInfo || !product.rocketRegInfo.schemaString) {
        return false;
      }

      const schema = JSON.parse(product.rocketRegInfo.schemaString);

      // productPage.commonAttributes.required 배열에서 해당 필드 확인
      if (schema.properties &&
          schema.properties.productPage &&
          schema.properties.productPage.properties &&
          schema.properties.productPage.properties.commonAttributes &&
          schema.properties.productPage.properties.commonAttributes.required) {

        return schema.properties.productPage.properties.commonAttributes.required.includes(fieldName);
      }

      return false;
    } catch (error) {
      console.warn(`스키마에서 ${fieldName} 필드 확인 실패:`, error);
      return false;
    }
  },

  /**
   * 가격 설정 결과 처리
   * @param {Object} product - 상품 객체
   * @param {Object} result - 가격 설정 결과
   * @returns {Promise<boolean>} 처리 성공 여부
   */
  // pricingService.js의 handleResult 메서드 수정
  async handleResult(product, result) {
    try {
      if (!product || !result) return false;

      // 결과 데이터 확인 및 변환
      const pricingData = result.pricing || result;

      // 상품 데이터 업데이트
      const updatedProduct = { ...product };
      updatedProduct.pricing = pricingData;
      updatedProduct.pricingStatus = 'completed';

      // 옵션 데이터도 업데이트
      if (updatedProduct.options && updatedProduct.options.length > 0 &&
        pricingData.options && pricingData.options.length > 0) {

        // 옵션 번호 기반으로 매칭하여 업데이트
        updatedProduct.options.forEach(option => {
          const matchedPricing = pricingData.options.find(p => p.optionNumber === option.optionNumber);
          if (matchedPricing) {
            // 옵션의 가격 정보 업데이트
            option.price = matchedPricing.price;
            option.bundleQuantity = matchedPricing.bundleQuantity;
            option.supplyPrice = matchedPricing.supplyPrice;
            option.sellingPrice = matchedPricing.sellingPrice;
            option.purchasePrice = matchedPricing.purchasePrice;
            option.supplyMargin = matchedPricing.supplyMargin;
            option.sellingMargin = matchedPricing.sellingMargin;

            // product 레벨의 coupangDoc 업데이트 (해당 옵션의 인덱스에 맞게)
            if (updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument) {
              // 옵션 인덱스 찾기
              const optionIndex = updatedProduct.options.findIndex(opt => opt.optionNumber === option.optionNumber);

              if (optionIndex !== -1 && optionIndex < updatedProduct.coupangDoc.jsonDocument.length) {
                const docObj = updatedProduct.coupangDoc.jsonDocument[optionIndex];

                if (docObj.productPage && docObj.productPage.commonAttributes) {
                  console.log(`옵션 ${option.optionNumber}에 해당하는 product.coupangDoc.jsonDocument[${optionIndex}] 가격 정보 업데이트:`);
                  console.log(`- 기존 purchasePrice: ${docObj.productPage.commonAttributes.purchasePrice}`);
                  console.log(`- 새 supplyPrice: ${matchedPricing.supplyPrice}`);
                  console.log(`- 기존 coupangSalePrice: ${docObj.productPage.commonAttributes.coupangSalePrice}`);
                  console.log(`- 새 sellingPrice: ${matchedPricing.sellingPrice}`);

                  // commonAttributes 가격 정보 업데이트
                  // 수정: purchasePrice 필드에는 supplyPrice 값을 사용
                  docObj.productPage.commonAttributes.purchasePrice = Number(Math.round(matchedPricing.supplyPrice));
                  docObj.productPage.commonAttributes.coupangSalePrice = Number(Math.round(matchedPricing.sellingPrice));

                  // MSRP는 판매가에 기본설정의 msrpMultiplier를 곱해서 설정
                  const settings = store.state.settings[0]?.defaultSettings || {};
                  const msrpMultiplier = settings.msrpMultiplier || 1.3;
                  const roundUnit = settings.roundUnit || 100;
                  const msrpValue = Number(Math.round((matchedPricing.sellingPrice * msrpMultiplier) / roundUnit) * roundUnit);

                  // 스키마 기반으로 msrp 또는 osrp 필드 결정
                  const shouldUseOsrp = this.checkSchemaRequiredField(updatedProduct, 'osrp');
                  const fieldName = shouldUseOsrp ? 'osrp' : 'msrp';
                  docObj.productPage.commonAttributes[fieldName] = msrpValue;

                  console.log(`- 업데이트 후 purchasePrice: ${docObj.productPage.commonAttributes.purchasePrice}`);
                  console.log(`- 업데이트 후 coupangSalePrice: ${docObj.productPage.commonAttributes.coupangSalePrice}`);
                } else {
                  console.warn(`옵션 ${option.optionNumber}에 해당하는 jsonDocument[${optionIndex}]에 productPage.commonAttributes가 없습니다.`);
                }
              } else {
                console.warn(`옵션 ${option.optionNumber}에 해당하는 인덱스(${optionIndex})가 coupangDoc.jsonDocument 범위를 벗어납니다.`);
              }
            }
          }
          option.pricingStatus = 'completed';
        });
      }

      // 단일 상품의 coupangDoc 업데이트
      if (updatedProduct.coupangDoc && updatedProduct.coupangDoc.jsonDocument &&
        updatedProduct.coupangDoc.jsonDocument.length > 0 && pricingData.options && pricingData.options.length > 0) {
        const docObj = updatedProduct.coupangDoc.jsonDocument[0];
        // 첫 번째 옵션의 가격 정보 사용
        const firstPricing = pricingData.options[0];
        if (docObj.productPage && docObj.productPage.commonAttributes && firstPricing) {
          console.log('단일 상품 coupangDoc 가격 정보 업데이트:');
          console.log(`- 기존 purchasePrice: ${docObj.productPage.commonAttributes.purchasePrice}`);
          console.log(`- 새 supplyPrice: ${firstPricing.supplyPrice}`);
          console.log(`- 기존 coupangSalePrice: ${docObj.productPage.commonAttributes.coupangSalePrice}`);
          console.log(`- 새 sellingPrice: ${firstPricing.sellingPrice}`);

          // commonAttributes 가격 정보 업데이트
          docObj.productPage.commonAttributes.purchasePrice = Number(Math.round(firstPricing.supplyPrice));
          docObj.productPage.commonAttributes.coupangSalePrice = Number(Math.round(firstPricing.sellingPrice));
          // MSRP는 판매가에 기본설정의 msrpMultiplier를 곱해서 설정
          const settings = store.state.settings[0]?.defaultSettings || {};
          const msrpMultiplier = settings.msrpMultiplier || 1.3;
          const roundUnit = settings.roundUnit || 100;
          const msrpValue = Number(Math.round((firstPricing.sellingPrice * msrpMultiplier) / roundUnit) * roundUnit);

          // 스키마 기반으로 msrp 또는 osrp 필드 결정
          const shouldUseOsrp = this.checkSchemaRequiredField(updatedProduct, 'osrp');
          const fieldName = shouldUseOsrp ? 'osrp' : 'msrp';
          docObj.productPage.commonAttributes[fieldName] = msrpValue;
          console.log(`단일 상품의 ${fieldName} 필드 업데이트: ${msrpValue}`);

          console.log(`- 업데이트 후 purchasePrice: ${docObj.productPage.commonAttributes.purchasePrice}`);
          console.log(`- 업데이트 후 coupangSalePrice: ${docObj.productPage.commonAttributes.coupangSalePrice}`);
        }
      }

      // 새로운 방식: 서비스에서 직접 부분 업데이트
      await this.updateProductWithCoupangDocPartial(updatedProduct, pricingData);
      console.log('가격 설정 정보로 상품 업데이트 완료');

      return true;
    } catch (error) {
      console.error('가격 설정 결과 처리 중 오류:', error);
      return false;
    }
  }
};

export default pricingService;